/**
 * FERP Portal - Unified Primitive Mutations Hook
 *
 * Provides CRUD mutations for available primitive types.
 * Uses generated GraphQL mutations with multi-tenant isolation.
 *
 * CONSTRAINTS:
 * - Repository pattern via generated GraphQL mutations
 * - Backend ID generation via useGenerateIdMutation
 * - Multi-tenant isolation via useTenantId()
 * - FERP Append-Only Architecture: Archive instead of delete
 *
 * NOTE: Not all primitives have mutations available.
 * This hook uses only what the backend provides.
 */

import { useCallback } from 'react';
import { useTenantId } from '@/auth';
import {
  // ID Generation
  useGenerateIdMutation,
  // DESIGN Phase mutations
  useCreateWorkflowMutation,
  useCreateConstraintMutation,
  useCreateDependencyMutation,
  useArchiveDependencyMutation,
  useCreateFormSchemaMutation,
  // CONFIGURE Phase mutations
  useCreateActorMutation,
  useArchiveActorMutation,
  useCreatePolicyMutation,
  useCreateQueueMutation,
  useArchiveQueueMutation,
  useCreateScheduleMutation,
  useArchiveScheduleMutation,
  // EXECUTE Phase mutations
  useCreateTaskMutation,
  useStartTaskMutation,
  useCompleteTaskMutation,
  useAssignTaskMutation,
  useCreateEventMutation,
  useStartSagaMutation,
  useExecuteDagWorkflowMutation,
  useCancelDagExecutionMutation,
  // MONITOR Phase mutations
  useStageSafetyEventMutation,
  useCreateAuditMutation,
  useArchiveAuditMutation,
  useCreateNotificationMutation,
  useArchiveNotificationMutation,
  // GOVERN Phase mutations
  useCreateContractMutation,
  useArchiveContractMutation,
  // Input types
  type GqlCreateWorkflowInput,
  type GqlCreateConstraintInput,
  type CreateDependencyInput,
  type FormSchemaInput,
  type GqlCreateActorInput,
  type CreatePolicyInput,
  type GqlCreateQueueInput,
  type GqlCreateScheduleInput,
  type GqlCreateTaskInput,
  type GqlCompleteTaskInput,
  type AssignTaskInput,
  type GqlCreateEventInput,
  type CreateSafetyEventInput,
  type CreateAuditInput,
  type GqlCreateNotificationInput,
  type GqlCreateContractInput,
  type StartSagaInput,
  type ExecuteDagWorkflowInput,
  // Enum types
  IdBrokerEntityType,
} from '@/generated/graphql';

// ============================================================================
// TYPES
// ============================================================================

export interface MutationResult<T> {
  data?: T;
  error?: Error;
}

export interface PrimitiveMutations {
  // ID Generation
  generateId: (entityType: IdBrokerEntityType, count?: number) => Promise<string[]>;
  // Create operations (available for most primitives)
  createWorkflow: (input: GqlCreateWorkflowInput) => Promise<MutationResult<{ id: string }>>;
  createConstraint: (input: GqlCreateConstraintInput) => Promise<MutationResult<{ id: string }>>;
  createDependency: (input: CreateDependencyInput) => Promise<MutationResult<{ id: string }>>;
  createFormSchema: (input: FormSchemaInput) => Promise<MutationResult<{ subgraph: string }>>;
  createActor: (input: GqlCreateActorInput) => Promise<MutationResult<{ id: string }>>;
  createPolicy: (input: CreatePolicyInput) => Promise<MutationResult<{ id: string }>>;
  createQueue: (input: GqlCreateQueueInput) => Promise<MutationResult<{ id: string }>>;
  createSchedule: (input: GqlCreateScheduleInput) => Promise<MutationResult<{ scheduleId: string }>>;
  createTask: (input: GqlCreateTaskInput) => Promise<MutationResult<{ id: string }>>;
  createEvent: (input: GqlCreateEventInput) => Promise<MutationResult<{ id: string }>>;
  createSafetyEvent: (input: CreateSafetyEventInput) => Promise<MutationResult<{ eventId: string }>>;
  createAudit: (input: CreateAuditInput) => Promise<MutationResult<{ id: string }>>;
  createNotification: (input: GqlCreateNotificationInput) => Promise<MutationResult<{ id: string }>>;
  createContract: (input: GqlCreateContractInput) => Promise<MutationResult<{ id: string }>>;
  // Archive operations (FERP Append-Only Architecture)
  archiveActor: (actorId: string, reason: string) => Promise<MutationResult<{ success: boolean }>>;
  archiveDependency: (dependencyId: string, reason: string) => Promise<MutationResult<{ success: boolean }>>;
  archiveQueue: (queueId: string, reason: string) => Promise<MutationResult<{ success: boolean }>>;
  archiveSchedule: (scheduleId: string, reason: string) => Promise<MutationResult<{ success: boolean }>>;
  archiveAudit: (auditId: string, reason: string) => Promise<MutationResult<{ success: boolean }>>;
  archiveNotification: (notificationId: string, reason: string) => Promise<MutationResult<{ success: boolean }>>;
  archiveContract: (workflowInstanceId: string, contractId: string, reason: string) => Promise<MutationResult<{ success: boolean }>>;
  // Task lifecycle
  startTask: (workflowInstanceId: string, taskId: string) => Promise<MutationResult<{ id: string }>>;
  completeTask: (workflowInstanceId: string, input: GqlCompleteTaskInput) => Promise<MutationResult<{ id: string }>>;
  assignTask: (queueId: string, input: AssignTaskInput) => Promise<MutationResult<{ id: string }>>;
  // Saga/DAG execution
  startSaga: (input: StartSagaInput) => Promise<MutationResult<{ executionId: string }>>;
  executeDagWorkflow: (input: ExecuteDagWorkflowInput) => Promise<MutationResult<{ success: boolean }>>;
  cancelDagExecution: (executionId: string) => Promise<MutationResult<{ success: boolean }>>;
  // Loading state
  loading: boolean;
}

// ============================================================================
// HOOK
// ============================================================================

export function usePrimitiveMutations(): PrimitiveMutations {
  const tenantId = useTenantId() ?? '';

  // ID Generation
  const [generateIdMutation, { loading: genLoading }] = useGenerateIdMutation();

  // DESIGN Phase
  const [createWorkflowMut, { loading: cwLoading }] = useCreateWorkflowMutation();
  const [createConstraintMut, { loading: cconLoading }] = useCreateConstraintMutation();
  const [createDependencyMut, { loading: cdepLoading }] = useCreateDependencyMutation();
  const [archiveDependencyMut, { loading: adepLoading }] = useArchiveDependencyMutation();
  const [createFormSchemaMut, { loading: cfLoading }] = useCreateFormSchemaMutation();

  // CONFIGURE Phase
  const [createActorMut, { loading: caLoading }] = useCreateActorMutation();
  const [archiveActorMut, { loading: aaLoading }] = useArchiveActorMutation();
  const [createPolicyMut, { loading: cpLoading }] = useCreatePolicyMutation();
  const [createQueueMut, { loading: cqLoading }] = useCreateQueueMutation();
  const [archiveQueueMut, { loading: aqLoading }] = useArchiveQueueMutation();
  const [createScheduleMut, { loading: csLoading }] = useCreateScheduleMutation();
  const [archiveScheduleMut, { loading: asLoading }] = useArchiveScheduleMutation();

  // EXECUTE Phase
  const [createTaskMut, { loading: ctLoading }] = useCreateTaskMutation();
  const [startTaskMut, { loading: stLoading }] = useStartTaskMutation();
  const [completeTaskMut, { loading: cptLoading }] = useCompleteTaskMutation();
  const [assignTaskMut, { loading: atLoading }] = useAssignTaskMutation();
  const [createEventMut, { loading: ceLoading }] = useCreateEventMutation();
  const [startSagaMut, { loading: ssLoading }] = useStartSagaMutation();
  const [executeDagMut, { loading: edLoading }] = useExecuteDagWorkflowMutation();
  const [cancelDagMut, { loading: cdLoading }] = useCancelDagExecutionMutation();

  // MONITOR Phase
  const [stageSafetyEventMut, { loading: sseLoading }] = useStageSafetyEventMutation();
  const [createAuditMut, { loading: cauLoading }] = useCreateAuditMutation();
  const [archiveAuditMut, { loading: aauLoading }] = useArchiveAuditMutation();
  const [createNotificationMut, { loading: cnLoading }] = useCreateNotificationMutation();
  const [archiveNotificationMut, { loading: anLoading }] = useArchiveNotificationMutation();

  // GOVERN Phase
  const [createContractMut, { loading: ccnLoading }] = useCreateContractMutation();
  const [archiveContractMut, { loading: acnLoading }] = useArchiveContractMutation();

  // Compute overall loading state
  const loading = genLoading || cwLoading || cconLoading || cdepLoading || adepLoading ||
    cfLoading || caLoading || aaLoading || cpLoading || cqLoading || aqLoading ||
    csLoading || asLoading || ctLoading || stLoading || cptLoading || atLoading || ceLoading ||
    ssLoading || edLoading || cdLoading || sseLoading || cauLoading || aauLoading ||
    cnLoading || anLoading || ccnLoading || acnLoading;

  // Generate IDs
  const generateId = useCallback(async (entityType: IdBrokerEntityType, count = 1): Promise<string[]> => {
    const result = await generateIdMutation({
      variables: { input: { entityType, count } },
    });
    // @ts-expect-error Did you mean
    if (result.errors?.[0]) {
      // @ts-expect-error Did you mean
      throw new Error(result.errors[0].message);
    }
    return result.data?.generateId?.ids ?? [];
  }, [generateIdMutation]);

  // Create operations
  const createWorkflow = useCallback(async (input: GqlCreateWorkflowInput): Promise<MutationResult<{ id: string }>> => {
    try {
      const result = await createWorkflowMut({ variables: { input } });
      return { data: { id: result.data?.createWorkflow?.id ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [createWorkflowMut]);

  const createConstraint = useCallback(async (input: GqlCreateConstraintInput): Promise<MutationResult<{ id: string }>> => {
    try {
      const result = await createConstraintMut({ variables: { input } });
      return { data: { id: result.data?.createConstraint?.id ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [createConstraintMut]);

  const createDependency = useCallback(async (input: CreateDependencyInput): Promise<MutationResult<{ id: string }>> => {
    try {
      const result = await createDependencyMut({ variables: { input } });
      return { data: { id: result.data?.createDependency?.id ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [createDependencyMut]);

  const createFormSchema = useCallback(async (input: FormSchemaInput): Promise<MutationResult<{ subgraph: string }>> => {
    try {
      const result = await createFormSchemaMut({ variables: { input } });
      return { data: { subgraph: result.data?.createFormSchema?.subgraph ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [createFormSchemaMut]);

  const createActor = useCallback(async (input: GqlCreateActorInput): Promise<MutationResult<{ id: string }>> => {
    try {
      const result = await createActorMut({ variables: { input } });
      return { data: { id: result.data?.createActor?.id ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [createActorMut]);

  const createPolicy = useCallback(async (input: CreatePolicyInput): Promise<MutationResult<{ id: string }>> => {
    try {
      const result = await createPolicyMut({ variables: { input } });
      return { data: { id: result.data?.createPolicy?.id ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [createPolicyMut]);

  const createQueue = useCallback(async (input: GqlCreateQueueInput): Promise<MutationResult<{ id: string }>> => {
    try {
      const result = await createQueueMut({ variables: { tenantId, input } });
      return { data: { id: result.data?.createQueue?.id ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [createQueueMut, tenantId]);

  const createSchedule = useCallback(async (input: GqlCreateScheduleInput): Promise<MutationResult<{ scheduleId: string }>> => {
    try {
      const result = await createScheduleMut({ variables: { input } });
      return { data: { scheduleId: result.data?.createSchedule?.scheduleId ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [createScheduleMut]);

  const createTask = useCallback(async (input: GqlCreateTaskInput): Promise<MutationResult<{ id: string }>> => {
    try {
      const result = await createTaskMut({ variables: { input } });
      return { data: { id: result.data?.createTask?.id ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [createTaskMut]);

  const createEvent = useCallback(async (input: GqlCreateEventInput): Promise<MutationResult<{ id: string }>> => {
    try {
      const result = await createEventMut({ variables: { tenantId, input } });
      return { data: { id: result.data?.createEvent?.id ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [createEventMut, tenantId]);

  const createSafetyEvent = useCallback(async (input: CreateSafetyEventInput): Promise<MutationResult<{ eventId: string }>> => {
    try {
      const result = await stageSafetyEventMut({ variables: { input } });
      return { data: { eventId: result.data?.stageSafetyEvent?.eventId ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [stageSafetyEventMut]);

  const createAudit = useCallback(async (input: CreateAuditInput): Promise<MutationResult<{ id: string }>> => {
    try {
      const result = await createAuditMut({ variables: { input } });
      return { data: { id: result.data?.createAudit?.id ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [createAuditMut]);

  const createNotification = useCallback(async (input: GqlCreateNotificationInput): Promise<MutationResult<{ id: string }>> => {
    try {
      const result = await createNotificationMut({ variables: { input } });
      return { data: { id: result.data?.createNotification?.id ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [createNotificationMut]);

  const createContract = useCallback(async (input: GqlCreateContractInput): Promise<MutationResult<{ id: string }>> => {
    try {
      const result = await createContractMut({ variables: { input } });
      return { data: { id: result.data?.createContract?.id ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [createContractMut]);

  // Archive operations (FERP Append-Only)
  const archiveActor = useCallback(async (actorId: string, reason: string): Promise<MutationResult<{ success: boolean }>> => {
    try {
      const result = await archiveActorMut({ variables: { actorId, reason } });
      return { data: { success: result.data?.archiveActor?.success ?? false } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [archiveActorMut]);

  const archiveDependency = useCallback(async (dependencyId: string, reason: string): Promise<MutationResult<{ success: boolean }>> => {
    try {
      const result = await archiveDependencyMut({ variables: { tenantId, dependencyId, reason } });
      return { data: { success: result.data?.archiveDependency?.success ?? false } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [archiveDependencyMut, tenantId]);

  const archiveQueue = useCallback(async (queueId: string, reason: string): Promise<MutationResult<{ success: boolean }>> => {
    try {
      const result = await archiveQueueMut({ variables: { tenantId, queueId, reason } });
      return { data: { success: result.data?.archiveQueue?.success ?? false } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [archiveQueueMut, tenantId]);

  const archiveSchedule = useCallback(async (scheduleId: string, reason: string): Promise<MutationResult<{ success: boolean }>> => {
    try {
      const result = await archiveScheduleMut({ variables: { scheduleId, reason } });
      return { data: { success: result.data?.archiveSchedule?.success ?? false } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [archiveScheduleMut]);

  const archiveAudit = useCallback(async (auditId: string, reason: string): Promise<MutationResult<{ success: boolean }>> => {
    try {
      const result = await archiveAuditMut({ variables: { tenantId, id: auditId, reason } });
      return { data: { success: result.data?.archiveAudit?.success ?? false } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [archiveAuditMut, tenantId]);

  const archiveNotification = useCallback(async (notificationId: string, reason: string): Promise<MutationResult<{ success: boolean }>> => {
    try {
      const result = await archiveNotificationMut({ variables: { notificationId, reason } });
      return { data: { success: result.data?.archiveNotification?.success ?? false } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [archiveNotificationMut]);

  const archiveContract = useCallback(async (workflowInstanceId: string, contractId: string, reason: string): Promise<MutationResult<{ success: boolean }>> => {
    try {
      const result = await archiveContractMut({ variables: { workflowInstanceId, contractId, reason } });
      return { data: { success: result.data?.archiveContract?.success ?? false } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [archiveContractMut]);

  // Task lifecycle
  const startTask = useCallback(async (workflowInstanceId: string, taskId: string): Promise<MutationResult<{ id: string }>> => {
    try {
      const result = await startTaskMut({ variables: { workflowInstanceId, taskId } });
      return { data: { id: result.data?.startTask?.id ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [startTaskMut]);

  const completeTask = useCallback(async (workflowInstanceId: string, input: GqlCompleteTaskInput): Promise<MutationResult<{ id: string }>> => {
    try {
      const result = await completeTaskMut({ variables: { workflowInstanceId, input } });
      return { data: { id: result.data?.completeTask?.id ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [completeTaskMut]);

  const assignTask = useCallback(async (queueId: string, input: AssignTaskInput): Promise<MutationResult<{ id: string }>> => {
    try {
      const result = await assignTaskMut({ variables: { tenantId, queueId, input } });
      return { data: { id: result.data?.assignTask?.id ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [assignTaskMut, tenantId]);

  // Saga/DAG execution
  const startSaga = useCallback(async (input: StartSagaInput): Promise<MutationResult<{ executionId: string }>> => {
    try {
      const result = await startSagaMut({ variables: { input } });
      return { data: { executionId: result.data?.startSaga?.executionId ?? '' } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [startSagaMut]);

  const executeDagWorkflow = useCallback(async (input: ExecuteDagWorkflowInput): Promise<MutationResult<{ success: boolean }>> => {
    try {
      const result = await executeDagMut({ variables: { input } });
      return { data: { success: result.data?.executeDagWorkflow?.success ?? false } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [executeDagMut]);

  const cancelDagExecution = useCallback(async (executionId: string): Promise<MutationResult<{ success: boolean }>> => {
    try {
      const result = await cancelDagMut({ variables: { executionId } });
      return { data: { success: result.data?.cancelDagExecution?.success ?? false } };
    } catch (error) {
      return { error: error as Error };
    }
  }, [cancelDagMut]);

  return {
    generateId,
    createWorkflow,
    createConstraint,
    createDependency,
    createFormSchema,
    createActor,
    createPolicy,
    createQueue,
    createSchedule,
    createTask,
    createEvent,
    createSafetyEvent,
    createAudit,
    createNotification,
    createContract,
    archiveActor,
    archiveDependency,
    archiveQueue,
    archiveSchedule,
    archiveAudit,
    archiveNotification,
    archiveContract,
    startTask,
    completeTask,
    assignTask,
    startSaga,
    executeDagWorkflow,
    cancelDagExecution,
    loading,
  };
}

export default usePrimitiveMutations;
